import React, { useState } from 'react';
import { Plus, Trash2, TrendingUp, Archive, ArchiveRestore } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import { formatCurrency, todayStr } from '../../utils/formatting';
import { getAllCurrencies, getCurrencyInfo } from '../../utils/currencies';
import { Modal, SearchableSelect, CategorySelect, ConfirmDialog, EmptyState, CardList } from '../Common/Common';
import { computeBalanceCostBasis } from '../../utils/calculations';
import { useIsPhone } from '../../hooks/useIsPhone';

function balanceInfo(balance, mainCurrency = 'ILS') {
  return computeBalanceCostBasis(balance, mainCurrency);
}

function Currency() {
  const {
    currencyBalances,
    addCurrencyBalance, deleteCurrencyBalance, updateCurrencyBalance,
    addCurrencyLot, deleteCurrencyLot, updateCurrencyLot,
    sellCurrencyBalance,
    addTransaction, categories, yearlyExpenses, addYearlyExpense,
    settings, showToast,
  } = useBudget();

  const fmt          = v => formatCurrency(v, settings);
  const mainCurrency = settings.currency || 'ILS';

  const [showAddBalance, setShowAddBalance] = useState(false);
  const [addLotFor,      setAddLotFor]      = useState(null);
  const [sellFor,        setSellFor]        = useState(null);
  const [detailFor,      setDetailFor]      = useState(null);
  const [showArchived,   setShowArchived]   = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(null);

  const activeBalances   = currencyBalances.filter(b => !b.archived);
  const archivedBalances = currencyBalances.filter(b => b.archived);
  const visibleBalances  = showArchived ? archivedBalances : activeBalances;

  const totalNisValue = activeBalances.reduce((s, b) => s + balanceInfo(b, mainCurrency).totalNis, 0);

  // Keep detail modal in sync when the underlying balance changes (e.g. after a buy/sell)
  const detailBalance = detailFor ? currencyBalances.find(b => b.id === detailFor.id) || null : null;

  return (
    <div>
      {/* Summary header */}
      <div className="card" style={{ marginBottom: 16, background: 'var(--bg-secondary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 4 }}>
              Total foreign holdings ({mainCurrency} equiv.)
            </div>
            <div className={`mono ${totalNisValue < 0 ? 'expense-text' : 'savings-text'}`} style={{ fontSize: 28, fontWeight: 500 }}>{fmt(totalNisValue)}</div>
            <div className="muted" style={{ fontSize: 12 }}>{activeBalances.length} balance{activeBalances.length !== 1 ? 's' : ''} tracked</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {archivedBalances.length > 0 && (
              <button className="btn btn-sm" onClick={() => setShowArchived(s => !s)}>
                {showArchived
                  ? <><ArchiveRestore size={13} /> Active ({activeBalances.length})</>
                  : <><Archive size={13} /> Archived ({archivedBalances.length})</>}
              </button>
            )}
            {!showArchived && (
              <button className="btn btn-primary" onClick={() => setShowAddBalance(true)}>
                <Plus size={14} /> Add currency
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Balance cards */}
      {visibleBalances.length === 0 ? (
        showArchived
          ? <EmptyState icon={<Archive size={28} />} title="No archived balances" sub="Archived balances appear here" />
          : <EmptyState
              icon={<span style={{ fontSize: 28 }}>💱</span>}
              title="No foreign currency balances"
              sub="Track EUR, USD, etc. with average cost basis and gain/loss on sale"
              action={<button className="btn btn-primary" onClick={() => setShowAddBalance(true)}><Plus size={14} /> Add currency</button>}
            />
      ) : (
        <div className="grid-auto">
          {visibleBalances.map(b => {
            const { totalForeign, totalNis, avgRate } = balanceInfo(b, mainCurrency);
            const fc   = getCurrencyInfo(b.currency);
            const symb = fc?.symbol || b.currency;

            return (
              <div
                key={b.id}
                className="card"
                onClick={() => setDetailFor(b)}
                style={{ cursor: 'pointer' }}
                title="Click to view details"
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{b.name || b.currency}</div>
                    <div className={`mono ${totalForeign < 0 ? 'expense-text' : 'savings-text'}`} style={{ fontSize: 22, fontWeight: 500 }}>
                      {totalForeign < 0 ? '−' : ''}{symb}{Math.abs(totalForeign).toFixed(2)}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>{fmt(totalNis)} {mainCurrency} equiv.</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 12 }}>
                  {avgRate > 0 && (
                    <><span className="muted">Avg rate</span><span className="mono" style={{ textAlign: 'right' }}>{avgRate.toFixed(3)}</span></>
                  )}
                  <><span className="muted">Currency</span><span style={{ textAlign: 'right' }}>{b.currency}</span></>
                  {(b.lots || []).length > 0 && (
                    <><span className="muted">Buy lots</span><span style={{ textAlign: 'right' }}>{(b.lots || []).length}</span></>
                  )}
                </div>

                <div className="divider" />
                <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  {!b.archived && (
                    <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setAddLotFor(b)}>+ Buy</button>
                  )}
                  {!b.archived && (
                    <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setSellFor(b)}>Sell</button>
                  )}
                  {b.archived
                    ? <button className="btn btn-ghost btn-icon btn-sm" title="Restore" onClick={() => { updateCurrencyBalance(b.id, { archived: false }); showToast(`${b.name || b.currency} restored`); }}><ArchiveRestore size={13} /></button>
                    : <button className="btn btn-ghost btn-icon btn-sm" title="Archive" onClick={() => { updateCurrencyBalance(b.id, { archived: true }); showToast(`${b.name || b.currency} archived`); }}><Archive size={13} /></button>
                  }
                  <button className="btn btn-ghost btn-icon btn-sm btn-danger" title="Delete" onClick={() => setConfirmDelete(b)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete balance"
          message={`Delete "${confirmDelete.name || confirmDelete.currency}"? All lot and spend history will be lost. This cannot be undone.`}
          danger
          onConfirm={() => { deleteCurrencyBalance(confirmDelete.id); showToast(`${confirmDelete.name || confirmDelete.currency} deleted`); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Add balance modal */}
      {showAddBalance && (
        <AddCurrencyBalanceModal
          mainCurrency={mainCurrency}
          onSave={data => { addCurrencyBalance(data); setShowAddBalance(false); showToast(`${data.currency} balance added`); }}
          onClose={() => setShowAddBalance(false)}
        />
      )}

      {/* Buy lot modal */}
      {addLotFor && (
        <AddLotModal
          balance={addLotFor}
          categories={categories}
          yearlyExpenses={yearlyExpenses}
          addYearlyExpense={addYearlyExpense}
          mainCurrency={mainCurrency}
          onSave={(lot, expenseTxn) => {
            addCurrencyLot(addLotFor.id, lot);
            if (expenseTxn) addTransaction(expenseTxn);
            const fc   = getCurrencyInfo(addLotFor.currency);
            const symb = fc?.symbol || addLotFor.currency;
            showToast(`Bought ${symb}${lot.foreignAmount}${expenseTxn ? ' — expense logged' : ''}`);
            setAddLotFor(null);
          }}
          onClose={() => setAddLotFor(null)}
          fmt={fmt}
        />
      )}

      {/* Sell modal */}
      {sellFor && (
        <SellModal
          balance={sellFor}
          info={balanceInfo(sellFor)}
          mainCurrency={mainCurrency}
          onSell={(amt, rate) => {
            const result = sellCurrencyBalance(sellFor.id, amt, rate);
            showToast(`Sold ${amt} at ${rate} — ${result.gainLoss >= 0 ? '+' : ''}${result.gainLoss.toFixed(2)} ${mainCurrency} vs avg`);
            setSellFor(null);
          }}
          onClose={() => setSellFor(null)}
          fmt={fmt}
        />
      )}

      {/* Currency detail modal */}
      {detailBalance && (
        <CurrencyDetailModal
          balance={detailBalance}
          mainCurrency={mainCurrency}
          fmt={fmt}
          onClose={() => setDetailFor(null)}
          onBuy={() => { setDetailFor(null); setAddLotFor(detailBalance); }}
          onSell={() => { setDetailFor(null); setSellFor(detailBalance); }}
          onDeleteLot={lotId => deleteCurrencyLot(detailBalance.id, lotId)}
          onUpdateLot={(lotId, updates) => updateCurrencyLot(detailBalance.id, lotId, updates)}
        />
      )}
    </div>
  );
}

/* ─── Add balance modal ─── */
function AddCurrencyBalanceModal({ mainCurrency, onSave, onClose }) {
  const [form, setForm] = useState({ currency: 'EUR', name: '' });
  const fc = getCurrencyInfo(form.currency);
  function submit() {
    if (!form.currency) return;
    const info = getCurrencyInfo(form.currency);
    onSave({
      currency: form.currency,
      symbol: info.symbol || form.currency,
      name: form.name || info.name || form.currency,
    });
  }
  return (
    <Modal
      title="Add foreign currency balance"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Add</button>
        </>
      }
    >
      <div className="form-group">
        <label>Currency</label>
        <SearchableSelect
          value={form.currency}
          onChange={code => setForm(p => ({ ...p, currency: code }))}
          options={getAllCurrencies()
            .filter(c => c.code !== mainCurrency)
            .map(c => ({ value: c.code, label: `${c.name} (${c.code})`, secondary: c.symbol }))
          }
          placeholder="Search currency…"
        />
      </div>
      <div className="form-group">
        <label>Label (optional)</label>
        <input
          value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          placeholder={fc?.name || form.currency}
        />
      </div>
    </Modal>
  );
}

/* ─── Buy lot modal ─── */
function AddLotModal({ balance, categories, yearlyExpenses = [], addYearlyExpense, mainCurrency, onSave, onClose, fmt }) {
  const fc   = getCurrencyInfo(balance.currency);
  const symb = fc?.symbol || balance.currency;

  const [form, setForm] = useState({
    date: todayStr(),
    foreignAmount: '',
    rate: '',
    notes: '',
    deductBalance: false,
    deductCategory: categories.filter(c => c.type !== 'income')[0]?.id || '',
  });

  const setF     = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const nisEquiv = (parseFloat(form.foreignAmount) || 0) * (parseFloat(form.rate) || 0);

  function submit() {
    if (!form.foreignAmount || !form.rate) return;
    const lot = {
      date: form.date,
      foreignAmount: parseFloat(form.foreignAmount),
      rate: parseFloat(form.rate),
      notes: form.notes,
    };
    let expenseTxn = null;
    if (form.deductBalance && nisEquiv > 0 && form.deductCategory) {
      expenseTxn = {
        date: form.date,
        type: 'expense',
        amount: parseFloat(nisEquiv.toFixed(2)),
        category: form.deductCategory,
        description: `Buy ${symb}${lot.foreignAmount} @ ${lot.rate}`,
        notes: form.notes,
      };
    }
    onSave(lot, expenseTxn);
  }

  return (
    <Modal
      title={`Buy ${balance.currency}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Add lot</button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: 1 }}>
          <label>Date</label>
          <input type="date" value={form.date} onChange={e => setF('date', e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Amount ({symb})</label>
          <input
            type="number" min="0" step="0.01"
            value={form.foreignAmount}
            onChange={e => setF('foreignAmount', e.target.value)}
            placeholder="0.00"
            autoFocus
          />
        </div>
      </div>

      <div className="form-group">
        <label>Rate ({mainCurrency} per {symb})</label>
        <input
          type="number" min="0" step="0.001"
          value={form.rate}
          onChange={e => setF('rate', e.target.value)}
          placeholder="e.g. 4.15"
        />
        {nisEquiv > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Cost: <strong>{fmt(nisEquiv)}</strong>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Notes (optional)</label>
        <input
          value={form.notes}
          onChange={e => setF('notes', e.target.value)}
          placeholder="e.g. Changed at airport"
        />
      </div>

      {/* Deduct from balance checkbox */}
      <div style={{
        borderTop: '1px solid var(--border)',
        paddingTop: 14,
        marginTop: 4,
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={form.deductBalance}
            onChange={e => setF('deductBalance', e.target.checked)}
            style={{ width: 15, height: 15, cursor: 'pointer' }}
          />
          <span style={{ fontSize: 14 }}>Deduct {mainCurrency} cost from main balance</span>
        </label>
        <div className="muted" style={{ fontSize: 12, marginTop: 4, marginLeft: 23 }}>
          Logs an expense transaction for {nisEquiv > 0 ? <strong>{fmt(nisEquiv)}</strong> : 'the NIS cost'} so your tracked balance reflects the purchase.
        </div>

        {form.deductBalance && (
          <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
            <label>Expense category</label>
            <CategorySelect
              value={form.deductCategory}
              onChange={id => setF('deductCategory', id)}
              categories={categories}
              yearlyExpenses={yearlyExpenses}
              type="expense"
              addYearlyExpense={addYearlyExpense}
              placeholder="Select category…"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ─── Currency Detail Modal ─── */
function CurrencyDetailModal({ balance, mainCurrency, fmt, onClose, onBuy, onSell, onDeleteLot, onUpdateLot }) {
  const { totalForeign, totalNis, avgRate } = balanceInfo(balance);
  const fc   = getCurrencyInfo(balance.currency);
  const symb = fc?.symbol || balance.currency;
  const lots   = balance.lots   || [];
  const spends = balance.spends || [];
  const isPhone = useIsPhone();
  const [editLot, setEditLot] = useState(null);

  const lotColumns = [
    { slot: 'title', render: lot => `${symb}${lot.foreignAmount.toFixed(2)}` },
    { slot: 'sub',   render: lot => `${lot.date} · @ ${lot.rate.toFixed(4)}${lot.notes ? ` · ${lot.notes}` : ''}` },
    { slot: 'value', render: lot => fmt(lot.foreignAmount * lot.rate), valueClass: () => 'savings-text' },
  ];
  const lotSlideLeft = [{
    label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
    confirm: { title: 'Delete lot', message: 'Delete this buy lot? This cannot be undone.', danger: true },
    onAction: lot => onDeleteLot(lot.id),
  }];
  const spendColumns = [
    { slot: 'title', render: sp => sp.description || 'Spend' },
    { slot: 'sub',   render: sp => `${sp.date || '—'}${sp.rate ? ` · @ ${sp.rate.toFixed(4)}` : ''}` },
    { slot: 'value', render: sp => <>−{symb}{(sp.foreignAmount || 0).toFixed(2)}</>, valueClass: () => 'expense-text' },
  ];

  return (
    <Modal
      title={`${balance.name || balance.currency} — Details`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Close</button>
          {!balance.archived && (
            <button className="btn btn-sm" onClick={onSell}>
              Sell {balance.currency}
            </button>
          )}
          {!balance.archived && (
            <button className="btn btn-primary" onClick={onBuy}>
              + Buy {balance.currency}
            </button>
          )}
        </>
      }
    >
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>Balance</div>
          <div className="mono savings-text" style={{ fontSize: 18, fontWeight: 500 }}>{symb}{totalForeign.toFixed(2)}</div>
        </div>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>{mainCurrency} equiv.</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 500 }}>{fmt(totalNis)}</div>
        </div>
        <div style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
          <div className="muted" style={{ fontSize: 11 }}>Avg rate</div>
          <div className="mono" style={{ fontSize: 18, fontWeight: 500 }}>{avgRate > 0 ? avgRate.toFixed(4) : '—'}</div>
        </div>
      </div>

      {/* Buy lots */}
      {lots.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
            Buy lots ({lots.length})
          </div>
          {isPhone ? (
            <CardList
              rows={lots}
              rowKey={lot => lot.id}
              columns={lotColumns}
              onTap={lot => setEditLot(lot)}
              slideLeft={lotSlideLeft}
            />
          ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount ({balance.currency})</th>
                  <th style={{ textAlign: 'right' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Cost ({mainCurrency})</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lots.map(lot => (
                  <tr key={lot.id}>
                    <td className="muted">{lot.date}</td>
                    <td className="amount-cell mono savings-text">{symb}{lot.foreignAmount.toFixed(2)}</td>
                    <td className="amount-cell mono">{lot.rate.toFixed(4)}</td>
                    <td className="amount-cell mono">{fmt(lot.foreignAmount * lot.rate)}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{lot.notes || '—'}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-icon btn-xs btn-danger"
                        onClick={() => onDeleteLot(lot.id)}
                        title="Delete lot"
                      >
                        <Trash2 size={10} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Spend / sell history */}
      {spends.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', marginBottom: 10 }}>
            Spend / sell history ({spends.length})
          </div>
          {isPhone ? (
            <CardList rows={[...spends].reverse()} rowKey={sp => sp.id} columns={spendColumns} />
          ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount ({balance.currency})</th>
                  <th style={{ textAlign: 'right' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Value ({mainCurrency})</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {[...spends].reverse().map(sp => (
                  <tr key={sp.id}>
                    <td className="muted">{sp.date || '—'}</td>
                    <td className="amount-cell mono expense-text">−{symb}{(sp.foreignAmount || 0).toFixed(2)}</td>
                    <td className="amount-cell mono">{sp.rate ? sp.rate.toFixed(4) : '—'}</td>
                    <td className="amount-cell mono">{sp.amount ? fmt(sp.amount) : '—'}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{sp.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {lots.length === 0 && spends.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
          No transactions yet — click "+ Buy {balance.currency}" to add your first lot.
        </div>
      )}

      {editLot && (
        <EditLotModal
          lot={editLot}
          symb={symb}
          mainCurrency={mainCurrency}
          fmt={fmt}
          onSave={updates => { onUpdateLot(editLot.id, updates); setEditLot(null); }}
          onClose={() => setEditLot(null)}
        />
      )}
    </Modal>
  );
}

/* ─── Edit lot modal (phone tap-to-edit) ─── */
function EditLotModal({ lot, symb, mainCurrency, fmt, onSave, onClose }) {
  const [form, setForm] = useState({
    date: lot.date || todayStr(),
    foreignAmount: String(lot.foreignAmount ?? ''),
    rate: String(lot.rate ?? ''),
    notes: lot.notes || '',
  });
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const fa = parseFloat(form.foreignAmount) || 0;
  const rate = parseFloat(form.rate) || 0;

  function save() {
    onSave({ date: form.date, foreignAmount: fa, rate, notes: form.notes });
  }

  return (
    <Modal
      title="Edit buy lot"
      variant="sheet"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!fa || !rate}>Save changes</button>
        </>
      }
    >
      <div className="form-group">
        <label>Date</label>
        <input type="date" value={form.date} onChange={e => upd('date', e.target.value)} />
      </div>
      <div className="flex" style={{ gap: 10 }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Amount ({symb})</label>
          <input type="number" min="0" step="0.01" inputMode="decimal"
            value={form.foreignAmount} onChange={e => upd('foreignAmount', e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Rate</label>
          <input type="number" min="0" step="0.0001" inputMode="decimal"
            value={form.rate} onChange={e => upd('rate', e.target.value)} />
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>Cost ≈ {fmt(fa * rate)} {mainCurrency}</div>
      <div className="form-group">
        <label>Notes</label>
        <input value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="optional" />
      </div>
    </Modal>
  );
}

/* ─── Sell modal ─── */
function SellModal({ balance, info, mainCurrency, onSell, onClose, fmt }) {
  const fc   = getCurrencyInfo(balance.currency);
  const symb = fc?.symbol || balance.currency;
  const [amount, setAmount] = useState('');
  const [rate,   setRate]   = useState('');
  const sellAmt  = parseFloat(amount) || 0;
  const sellRate = parseFloat(rate) || 0;
  const nisReceived = sellAmt * sellRate;
  const nisAtAvg    = sellAmt * info.avgRate;
  const gainLoss    = nisReceived - nisAtAvg;

  return (
    <Modal
      title={`Sell ${balance.currency}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => sellAmt > 0 && sellRate > 0 && onSell(sellAmt, sellRate)}
            disabled={!sellAmt || !sellRate}
          >
            Confirm sale
          </button>
        </>
      }
    >
      <div style={{
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--bg-secondary)', marginBottom: 14, fontSize: 13,
      }}>
        Balance: <strong>{symb}{info.totalForeign.toFixed(2)}</strong>
        {' · '}avg rate: <strong>{info.avgRate.toFixed(3)} {mainCurrency}/{symb}</strong>
        {' · '}value: <strong>{fmt(info.totalNis)}</strong>
      </div>

      <div style={{ display: 'flex', gap: 10 }} className="form-group">
        <div style={{ flex: 1 }}>
          <label>Amount to sell ({symb})</label>
          <input
            type="number" min="0" step="0.01" max={info.totalForeign}
            value={amount} onChange={e => setAmount(e.target.value)}
            placeholder={`max ${info.totalForeign.toFixed(2)}`}
            autoFocus
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>Sell rate ({mainCurrency} per {symb})</label>
          <input
            type="number" min="0" step="0.001"
            value={rate} onChange={e => setRate(e.target.value)}
            placeholder="actual bank rate"
          />
        </div>
      </div>

      {sellAmt > 0 && sellRate > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 13, marginTop: 8 }}>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>{mainCurrency} received</div>
            <div className="mono income-text">{fmt(nisReceived)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>Value at avg</div>
            <div className="mono">{fmt(nisAtAvg)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11 }}>Gain / loss</div>
            <div className={`mono ${gainLoss >= 0 ? 'income-text' : 'expense-text'}`}>
              {gainLoss >= 0 ? '+' : ''}{fmt(gainLoss)}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default React.memo(Currency);
